import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const INSTRUCTOR_PHOTO =
  "https://static.readdy.ai/image/99ee4fd5eb46fc3e1cae300823aab6a6/d0af4d7d162fec6e5a578befc0a6cad3.jpeg";

const credentials = [
  { icon: "ri-nurse-line", label: "Registered Nurse (RN)" },
  { icon: "ri-shield-check-line", label: "AHA Certified Instructor" },
  { icon: "ri-heart-pulse-line", label: "BLS / ACLS / PALS Instructor" },
  { icon: "ri-hospital-line", label: "Clinical Experience" },
];

const highlights = [
  { icon: "ri-medal-2-line", value: "AHA", label: "Aligned Courses" },
  { icon: "ri-time-line", value: "Same-Day", label: "Certification" },
  { icon: "ri-group-line", value: "500+", label: "Students Trained" },
  { icon: "ri-trophy-line", value: "5★", label: "Rated Instructor" },
];

export default function InstructorFeature() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-0"
      style={{ background: "linear-gradient(135deg, #0d0d1a 0%, #1a0a2e 40%, #0f0f0f 100%)" }}
    >
      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" />

      {/* Subtle background orbs */}
      <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, #6B46C1 0%, transparent 70%)" }} />
      <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full opacity-8 pointer-events-none"
        style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-0 items-stretch min-h-[680px]">

          {/* ── LEFT: Photo Panel ── */}
          <div
            className="relative flex items-end py-16 lg:py-0 transition-all duration-1000"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-60px)",
            }}
          >
            {/* Decorative gold frame */}
            <div
              className="absolute inset-8 lg:inset-12 rounded-2xl pointer-events-none"
              style={{ border: "1px solid rgba(212,175,55,0.2)" }}
            />

            {/* Photo */}
            <div className="relative w-full lg:max-w-[440px] mx-auto">
              {/* Gold glow behind photo */}
              <div
                className="absolute -inset-2 rounded-3xl opacity-30 pointer-events-none"
                style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(107,70,193,0.15))" }}
              />

              <div
                className="relative rounded-2xl overflow-hidden"
                style={{ border: "2px solid rgba(212,175,55,0.4)" }}
              >
                <img
                  src={INSTRUCTOR_PHOTO}
                  alt="Destiny — Lead RN Instructor, Cardiocraft CPR Kissimmee"
                  className="w-full object-cover object-top"
                  style={{ height: "580px" }}
                />
                {/* Subtle bottom gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
                  style={{ background: "linear-gradient(to top, rgba(13,13,26,0.85) 0%, transparent 100%)" }} />

                {/* Bottom badge */}
                <div className="absolute bottom-5 left-5 right-5">
                  <div
                    className="flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-md"
                    style={{ background: "rgba(13,13,26,0.75)", border: "1px solid rgba(212,175,55,0.3)" }}
                  >
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
                      style={{ background: "linear-gradient(135deg, #D4AF37, #b8952e)" }}
                    >
                      <i className="ri-nurse-line text-black text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm leading-none mb-0.5">
                        Registered Nurse &amp; Lead Instructor
                      </p>
                      <p className="text-brand-gold text-xs font-medium">Cardiocraft CPR — Kissimmee, FL</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating credential tag */}
              <div
                className="absolute -top-4 -right-4 flex items-center gap-2 px-3 py-2 rounded-full"
                style={{
                  background: "linear-gradient(135deg, #6B46C1, #4C1D95)",
                  border: "1px solid rgba(212,175,55,0.5)",
                  boxShadow: "0 4px 20px rgba(107,70,193,0.4)",
                }}
              >
                <i className="ri-shield-check-fill text-brand-gold text-sm" />
                <span className="text-white text-xs font-bold whitespace-nowrap">AHA Instructor</span>
              </div>

              {/* Star rating floating badge */}
              <div
                className="absolute -left-4 top-20 flex flex-col items-center gap-1 px-3 py-3 rounded-xl"
                style={{
                  background: "rgba(212,175,55,0.12)",
                  border: "1px solid rgba(212,175,55,0.4)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-brand-gold text-xs" />
                  ))}
                </div>
                <span className="text-white text-xs font-semibold">5-Star</span>
                <span className="text-white/50 text-xs">Rated</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Content Panel ── */}
          <div
            className="relative flex flex-col justify-center py-16 lg:py-20 lg:pl-16 transition-all duration-1000 delay-200"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(60px)",
            }}
          >
            {/* Section eyebrow */}
            <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6 self-start">
              <i className="ri-user-heart-line" />
              Meet Your Instructor
            </div>

            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
              Learn CPR from a{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #D4AF37, #f5d97a, #D4AF37)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Registered Nurse
              </span>{" "}
              Who Has Lived It
            </h2>

            {/* Personal quote */}
            <div
              className="relative pl-5 py-1 my-6"
              style={{ borderLeft: "3px solid #D4AF37" }}
            >
              <p className="text-white/80 text-lg leading-relaxed italic">
                &ldquo;I became a CPR instructor because I&apos;ve seen what happens when people aren&apos;t trained.
                My goal is simple: make sure every student leaves here confident, certified, and ready to save a life.&rdquo;
              </p>
              <p className="text-brand-gold text-sm font-bold mt-2 not-italic">
                — Destiny, Cardiocraft CPR
              </p>
            </div>

            <p className="text-white/65 text-base leading-relaxed mb-8">
              Every Cardio Craft CPR class is personally designed and led to give you the skills, confidence, and certification you need — all in a single day.
            </p>

            {/* Credentials list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {credentials.map((cred) => (
                <div
                  key={cred.label}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(212,175,55,0.18)",
                  }}
                >
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0"
                    style={{ background: "rgba(107,70,193,0.3)", border: "1px solid rgba(107,70,193,0.4)" }}
                  >
                    <i className={`${cred.icon} text-brand-gold text-base`} />
                  </div>
                  <span className="text-white/85 text-sm font-medium">{cred.label}</span>
                </div>
              ))}
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-3 mb-8">
              {highlights.map((h) => (
                <div
                  key={h.label}
                  className="text-center py-3 px-2 rounded-xl"
                  style={{
                    background: "rgba(107,70,193,0.12)",
                    border: "1px solid rgba(212,175,55,0.15)",
                  }}
                >
                  <p className="font-serif font-bold text-white text-lg leading-none mb-1">{h.value}</p>
                  <p className="text-white/45 text-xs uppercase tracking-wide leading-tight">{h.label}</p>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3">
              <Link
                to="/booking#reserve-spot"
                className="flex items-center gap-2 font-bold text-sm px-6 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #b8952e)",
                  color: "#1A1A1A",
                  boxShadow: "0 4px 20px rgba(212,175,55,0.35)",
                }}
              >
                <i className="ri-calendar-check-line" />
                Book a Class
              </Link>
              <Link
                to="/about"
                className="flex items-center gap-2 font-semibold text-sm px-6 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-white/10"
                style={{
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                Our Full Story
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}